import { useCallback, useState } from "react";
import {
  crearFormacion,
  actualizarFormacion,
  eliminarFormacion,
} from "../../../../services/formacionesService";
import { normalizeFormacion } from "../adapters/formacionesAdapter";
import { showToast } from "../../../../utils/showToast";

const initialForm = {
  estudiante_id: "",
  matricula_id: "",
  curso_id: "",
  nivel_id: "",
  formacion_origen_id: "",
  centro_formacion: "",
  es_reentrenamiento: 0,
  fecha_inicio: "",
  fecha_fin: "",
  fecha_aprobacion: "",
  estado: "inscrito",
  activo: true,
};

const validar = (form) => {
  if (form.matricula_id) return null;
  if (!form.estudiante_id) return "Selecciona un estudiante.";
  if (!form.curso_id) return "Selecciona un curso.";
  if (!String(form.centro_formacion || "").trim()) return "Ingresa el centro donde realizo la formacion.";
  if (form.es_reentrenamiento && !form.formacion_origen_id) {
    return "Debes elegir una formación base acreditada para el reentrenamiento.";
  }
  if (form.estado === "acreditado" && !form.fecha_aprobacion) {
    return "La fecha de aprobación es obligatoria para formaciones acreditadas.";
  }
  return null;
};

export default function useFormacionForm({ onCreated, onUpdated, onDeleted } = {}) {
  const [modal, setModal] = useState("");
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [formNuevo, setFormNuevo] = useState(initialForm);
  const [formEditar, setFormEditar] = useState(initialForm);
  const [formacionEditando, setFormacionEditando] = useState(null);
  // Cuando "Nueva formación" se abre desde el perfil de un estudiante (en vez del
  // módulo Formaciones), el estudiante ya se conoce — se bloquea el campo en el
  // modal en vez de mostrar el buscador. Ver FormacionFormModal > estudianteFijo.
  const [estudianteFijo, setEstudianteFijo] = useState(null);

  const abrirCrear = useCallback((prefill = null) => {
    if (prefill?.estudianteId) {
      setFormNuevo({ ...initialForm, estudiante_id: String(prefill.estudianteId) });
      setEstudianteFijo({
        id: prefill.estudianteId,
        nombre: prefill.estudianteNombre || `Estudiante #${prefill.estudianteId}`,
      });
    } else {
      setFormNuevo(initialForm);
      setEstudianteFijo(null);
    }
    setModal("crear");
  }, []);

  const abrirEditar = useCallback((formacion) => {
    if (!formacion) return;
    const parsed = normalizeFormacion(formacion);
    setFormacionEditando(parsed);
    setFormEditar({
      estudiante_id: parsed.estudianteId != null ? String(parsed.estudianteId) : "",
      matricula_id: parsed.matriculaId != null ? String(parsed.matriculaId) : "",
      curso_id: parsed.cursoId != null ? String(parsed.cursoId) : "",
      nivel_id: parsed.nivelId != null ? String(parsed.nivelId) : "",
      formacion_origen_id: parsed.formacionOrigenId != null ? String(parsed.formacionOrigenId) : "",
      centro_formacion: parsed.centroFormacion || "",
      fecha_inicio: parsed.fechaInicio || "",
      fecha_fin: parsed.fechaFin || "",
      fecha_aprobacion: parsed.fechaAprobacion || "",
      estado: parsed.estado || "inscrito",
      activo: Boolean(parsed.activo),
    });
    setModal("editar");
  }, []);

  const abrirEliminar = useCallback((formacion) => {
    if (!formacion) return;
    setFormacionEditando(normalizeFormacion(formacion));
    setModal("eliminar");
  }, []);

  const cerrarModal = useCallback(() => {
    setModal("");
    setFormacionEditando(null);
    setFormNuevo(initialForm);
    setFormEditar(initialForm);
    setEstudianteFijo(null);
  }, []);

  const handleCrear = useCallback(async () => {
    const validation = validar(formNuevo);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    setSaving(true);
    try {
      const payloadBase = {
        fecha_inicio: formNuevo.fecha_inicio || null,
        fecha_fin: formNuevo.fecha_fin || null,
        fecha_aprobacion: formNuevo.fecha_aprobacion || null,
        estado: formNuevo.estado,
        activo: formNuevo.activo ? 1 : 0,
        centro_formacion: formNuevo.centro_formacion?.trim() || null,
        curso_id: formNuevo.curso_id ? Number(formNuevo.curso_id) : undefined,
        nivel_id: formNuevo.nivel_id ? Number(formNuevo.nivel_id) : undefined,
      };

      const payload = formNuevo.matricula_id
        ? {
            ...payloadBase,
            matricula_id: Number(formNuevo.matricula_id),
          }
        : {
            ...payloadBase,
            estudiante_id: Number(formNuevo.estudiante_id),
            curso_id: Number(formNuevo.curso_id),
            formacion_origen_id: formNuevo.formacion_origen_id ? Number(formNuevo.formacion_origen_id) : undefined,
          };

      const created = await crearFormacion(payload);
      const createdNormalized = normalizeFormacion({
        ...payload,
        id: typeof created === "number" ? created : created?.id ?? payload.id,
      });
      onCreated?.(createdNormalized);
      showToast({ type: "success", message: "Formación creada correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo crear la formación." });
    } finally {
      setSaving(false);
    }
  }, [formNuevo, onCreated, cerrarModal]);

  const handleActualizar = useCallback(async () => {
    if (!formacionEditando) return;
    const validation = validar(formEditar);
    if (validation) {
      showToast({ type: "warning", message: validation });
      return;
    }
    setSaving(true);
    try {
      const payloadBase = {
        fecha_inicio: formEditar.fecha_inicio || null,
        fecha_fin: formEditar.fecha_fin || null,
        fecha_aprobacion: formEditar.fecha_aprobacion || null,
        estado: formEditar.estado,
        activo: formEditar.activo ? 1 : 0,
        centro_formacion: formEditar.centro_formacion?.trim() || null,
        curso_id: formEditar.curso_id ? Number(formEditar.curso_id) : undefined,
        nivel_id: formEditar.nivel_id ? Number(formEditar.nivel_id) : undefined,
      };

      const payload = formEditar.matricula_id
        ? {
            ...payloadBase,
            matricula_id: Number(formEditar.matricula_id),
          }
        : {
            ...payloadBase,
            estudiante_id: formEditar.estudiante_id ? Number(formEditar.estudiante_id) : undefined,
            curso_id: formEditar.curso_id ? Number(formEditar.curso_id) : undefined,
            formacion_origen_id: formEditar.formacion_origen_id ? Number(formEditar.formacion_origen_id) : undefined,
          };
      await actualizarFormacion(formacionEditando.id, payload);
      const updatedNormalized = normalizeFormacion({
        ...formacionEditando,
        ...payload,
      });
      onUpdated?.(updatedNormalized);
      showToast({ type: "success", message: "Formación actualizada correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo actualizar la formación." });
    } finally {
      setSaving(false);
    }
  }, [formEditar, formacionEditando, onUpdated, cerrarModal]);

  const handleEliminar = useCallback(async () => {
    if (!formacionEditando) return;
    setDeleting(true);
    try {
      await eliminarFormacion(formacionEditando.id);
      onDeleted?.(formacionEditando);
      showToast({ type: "success", message: "Formación eliminada correctamente." });
      cerrarModal();
    } catch (err) {
      console.error(err);
      showToast({ type: "danger", message: err.message || "No se pudo eliminar la formación." });
    } finally {
      setDeleting(false);
    }
  }, [formacionEditando, onDeleted, cerrarModal]);

  return {
    modal,
    formNuevo,
    setFormNuevo,
    formEditar,
    setFormEditar,
    formacionEditando,
    estudianteFijo,
    saving,
    deleting,
    abrirCrear,
    abrirEditar,
    abrirEliminar,
    cerrarModal,
    handleCrear,
    handleActualizar,
    handleEliminar,
  };
}
